import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, Trophy } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback targets (mirrors backend WRITING_WORD_TARGETS) — used if the API
// hasn't loaded yet, so the bar isn't empty on first keystroke.
const DEFAULT_TARGETS = { A1: 30, A2: 50, B1: 80, B2: 120, C1: 180 };
const DEFAULT_BONUS = 5;

const count = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);

/**
 * Live word counter + milestone progress bar shown under the Schreiben textarea.
 * Rewards students with +5 points when they reach the CEFR-level target.
 *
 * Props:
 *   level   — student's spanish_level ("A1"…"C1")
 *   text    — current textarea content
 */
export default function WritingMilestoneMeter({ level = 'A2', text = '' }) {
  const [targets, setTargets] = useState(DEFAULT_TARGETS);
  const [bonus, setBonus] = useState(DEFAULT_BONUS);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/writing/targets`, { withCredentials: true });
        if (r.data?.targets) setTargets(r.data.targets);
        if (typeof r.data?.bonus === 'number') setBonus(r.data.bonus);
      } catch { /* fallback to defaults */ }
    })();
  }, []);

  const target = targets[level] || targets.A2 || 50;
  const words = count(text);
  const pct = Math.min(100, Math.round((words / target) * 100));
  const reached = words >= target;

  return (
    <div className="mt-2 mb-4" data-testid="writing-milestone-meter">
      <div className="flex items-center justify-between text-xs font-medium mb-1.5">
        <span className="text-[#57534E] flex items-center gap-1.5">
          <span className={`level-badge ${level} text-[10px]`}>{level}</span>
          <span data-testid="writing-meter-count">
            <strong className={reached ? 'text-emerald-700' : 'text-[#292524]'}>{words}</strong>
            {' '}/ {target} {words === 1 ? 'Wort' : 'Wörter'}
          </span>
        </span>
        {reached ? (
          <span
            className="inline-flex items-center gap-1 text-emerald-700 font-semibold"
            data-testid="writing-meter-reached"
          >
            <CheckCircle size={14} weight="fill" /> +{bonus} Bonus
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[#78716C]">
            <Trophy size={14} /> +{bonus} bei {target} Wörtern
          </span>
        )}
      </div>
      <div className="h-2 w-full bg-[#F5F5F4] rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemax={100}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${pct}%`,
            backgroundColor: reached ? '#16a34a' : pct >= 66 ? '#84cc16' : pct >= 33 ? '#eab308' : '#AB1C24',
          }}
          data-testid="writing-meter-bar"
        />
      </div>
    </div>
  );
}
